<template>
  <div class="modal-overlay">
    <div class="modal-background" @click="$emit('closeModal')" />
    <div class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-heading">
            <h1 class="modal-title">{{ adminText('workflowsAll.finalize_payment_and_details_8a34cfb', 'Finalize Payment and Details') }}</h1>
            <p class="modal-description">{{ workflowLabel(modalDescription) }}</p>
          </div>
          <button
            class="close-button"
            type="button"
            :aria-label="adminText('workflowsAll.close_bbfa773', 'Close')"
            @click="$emit('closeModal')"
          >
            <v-icon color="#666666">close</v-icon>
          </button>
        </div>

        <label class="field payment-field">
          <span class="floating-label">{{ adminText('workflowsAll.payment_method_0e55fb3', 'Payment method') }}{{ workflowLabel(ticketPersonalization ? '*' : '') }}</span>
          <select v-model="paymentMethod" class="field-control" @change="calculateOrderTotalPrice">
            <option v-for="option in paymentOptions" :key="option.value" :value="option.value">
              {{ workflowLabel(option.display) }}
            </option>
          </select>
          <v-icon class="select-arrow" color="#979797" size="20">arrow_drop_down</v-icon>
        </label>
        <p class="payment-summary">
          {{ adminText('workflowsAll.customer_should_pay_f2101af', 'Customer should pay:') }} <b>{{ workflowLabel(totalPrice) }} EUR</b>
        </p>

        <div v-if="ticketPersonalization" class="ticket-owner-list">
          <div v-for="ticket in ticketRows" :key="ticket.key" class="ticket-owner-card">
            <div class="ticket-owner-header">
              <h2 class="ticket-owner-title">{{ adminText('workflowsAll.ticket_a7677e8', 'Ticket') }} {{ workflowLabel(ticket.name) }} #{{ workflowLabel(ticket.number) }}</h2>
              <v-icon :color="isTicketOwnerComplete(ticket.key) ? '#55A35D' : '#666666'">
                assignment_ind
              </v-icon>
            </div>
            <div class="ticket-owner-fields">
              <label
                class="field owner-field"
                :class="{ invalid: showOwnerError(ticket.key, 'firstName') }"
              >
                <span
                  v-if="ticketOwners[ticket.key] && ticketOwners[ticket.key].firstName"
                  class="floating-label"
                >
                  {{ adminText('workflowsAll.first_name_3cba86b', 'First name*') }}
                </span>
                <input
                  class="field-control"
                  :value="ticketOwners[ticket.key] && ticketOwners[ticket.key].firstName"
                  :placeholder="
                    workflowLabel(ticketOwners[ticket.key] && ticketOwners[ticket.key].firstName
                      ? ''
                      : adminText('workflowsAll.first_name_3cba86b', 'First name*'))
                  "
                  @input="updateTicketOwner(ticket.key, 'firstName', $event.target.value)"
                >
              </label>
              <label
                class="field owner-field"
                :class="{ invalid: showOwnerError(ticket.key, 'lastName') }"
              >
                <span
                  v-if="ticketOwners[ticket.key] && ticketOwners[ticket.key].lastName"
                  class="floating-label"
                >
                  {{ adminText('workflowsAll.last_name_b649dc9', 'Last name*') }}
                </span>
                <input
                  class="field-control"
                  :value="ticketOwners[ticket.key] && ticketOwners[ticket.key].lastName"
                  :placeholder="
                    workflowLabel(ticketOwners[ticket.key] && ticketOwners[ticket.key].lastName
                      ? ''
                      : adminText('workflowsAll.last_name_b649dc9', 'Last name*'))
                  "
                  @input="updateTicketOwner(ticket.key, 'lastName', $event.target.value)"
                >
              </label>
            </div>
          </div>
        </div>

        <div class="email-field-group">
          <label class="field email-field" :class="{ invalid: $v.buyerEmail.$error }">
            <img src="/assets/mail-icon.svg" alt="" class="mail-icon">
            <input
              v-model="buyerEmail"
              class="field-control"
              :placeholder="adminText('workflowsAll.buyer_s_mail_optional_4ea60f1', 'Buyer\'s mail (optional)')"
              type="email"
              @blur="$v.buyerEmail.$touch()"
            >
          </label>
          <p class="field-hint">
            {{ adminText('workflowsAll.by_adding_the_buyer_s_email_there_will_be_a_c_3628276', 'By adding the buyer\'s email, there will be a chance for CoreEvent to get in touch with the buyer in case of any changes.') }}
          </p>
        </div>
        <text-field
          v-if="showTicketAdditionalNote"
          v-model.trim="ticketAdditionalNote"
          :label="adminText('workflowsAll.additional_ticket_note_7c7ea9c', 'Additional ticket note')"
          :hint="workflowLabel((adminText('workflowsAll.available_characters_ca9295d', 'Available characters: ') + (remainingTicketAdditionalNoteCharacters) + ''))"
          persistent-hint
          :validator="$v.ticketAdditionalNote"
          :error-messages="{
            maxLength: adminText('workflowsAll.maximum_count_characters_allowed_f2e5548', 'Maximum {{count}} characters allowed.', { count: ticketAdditionalNoteMaxLength }),
          }"
          :maxlength="ticketAdditionalNoteMaxLength"
          :disabled="ticketAdditionalNoteMaxLength === 0"
          style="margin-bottom: 1rem"
        />
      </div>
      <div class="modal-actions">
        <button class="continue-button" type="button" @click="submit">{{ adminText('workflowsAll.continue_2e02623', 'Continue') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { email, maxLength } from 'vuelidate/lib/validators'
import { round } from 'lodash'

export default {
  mixins: [WorkflowDisplay],
  name: 'payment-type-modal',
  props: {
    orderTotalPrice: Number,
    eventId: String,
    ticketPersonalization: Boolean,
    order: {
      type: Array,
      default: () => [],
    },
    showTicketAdditionalNote: {
      type: Boolean,
      default: false,
    },
    ticketAdditionalNoteMaxLength: {
      type: Number,
      default: 86,
    },
    usePopustLabel: {
      type: Boolean,
      default: false,
    },
  },

  validations() {
    return {
      buyerEmail: { email },
      ticketAdditionalNote: {
        maxLength: maxLength(this.ticketAdditionalNoteMaxLength),
      },
    }
  },
  data() {
    const customPaymentOptions = [
      {
        display: 'workflowsAll.cash_fac448c',
        value: 'CASH',
      },
      {
        display: 'workflowsAll.card_158096b',
        value: 'CARD',
      },
      {
        display: 'workflowsAll.gratis_00f5fed',
        value: 'FREE',
      },
      {
        display: 'workflowsAll.cash_20_popusta_2394f3f',
        value: 'CASH20',
      },
      {
        display: 'workflowsAll.card_20_popusta_e0ab2a1',
        value: 'CARD20',
      },
      {
        display: 'workflowsAll.cash_50_popusta_8775261',
        value: 'CASH50',
      },
      {
        display: 'workflowsAll.card_50_popusta_7914648',
        value: 'CARD50',
      },
    ]
    const defaultPaymentOptions = [
      {
        display: 'workflowsAll.cash_fac448c',
        value: 'CASH',
      },
      {
        display: 'workflowsAll.card_158096b',
        value: 'CARD',
      },
      {
        display: 'workflowsAll.other_e08d9ea',
        value: 'OTHER',
      },
      {
        display: 'workflowsAll.gratis_00f5fed',
        value: 'FREE',
      },
      {
        display: 'workflowsAll.keks_10_2253461',
        value: 'KEKS10',
      },
      this.usePopustLabel
        ? {
          display: 'workflowsAll.popust_20_a38f39e',
          value: 'POPUST20',
        }
        : {
          display: 'workflowsAll.telegram_20_f7c5dcc',
          value: 'TELEGRAM20',
        },
      {
        display: 'workflowsAll.popust_40_7df37df',
        value: 'Popust40',
      },
      {
        display: 'workflowsAll.popust_50_eeece1c',
        value: 'Popust50',
      },
    ]

    return {
      paymentMethod: 'CASH',
      paymentOptions: this.eventId === '2603' ? customPaymentOptions : defaultPaymentOptions,
      buyerEmail: null,
      ticketOwners: {},
      ownerFieldsTouched: false,
      ticketAdditionalNote: '',
      totalPrice: this.orderTotalPrice,
    }
  },
  computed: {
    remainingTicketAdditionalNoteCharacters() {
      return Math.max(this.ticketAdditionalNoteMaxLength - (this.ticketAdditionalNote || '').length, 0)
    },
    modalDescription() {
      if (this.ticketPersonalization) {
        return (
          this.adminText('workflowsAll.to_finalize_the_order_select_a_payment_method_e1347a9', 'To finalize the order, select a payment method. The organizer requires ticket personalization with each ticket\'s first and last name. Email is optional but recommended for event updates.')
        )
      }

      return this.adminText('workflowsAll.to_finalize_the_order_select_a_payment_method_4db8533', 'To finalize the order, select a payment method. Email is optional but recommended for event updates.')
    },
    ticketRows() {
      const counts = {}

      return this.order.map((ticket, index) => {
        counts[ticket.id] = (counts[ticket.id] || 0) + 1

        return {
          key: `${ticket.id}-${index}`,
          id: ticket.id,
          name: ticket.name,
          number: counts[ticket.id],
          index,
        }
      })
    },
    ticketOwnerPayload() {
      return this.ticketRows.map(ticket => {
        const owner = this.ticketOwners[ticket.key] || {}

        return {
          orderItemId: ticket.id,
          firstName: (owner.firstName || '').trim(),
          lastName: (owner.lastName || '').trim(),
        }
      })
    },
  },
  watch: {
    ticketRows: {
      immediate: true,
      handler(rows) {
        rows.forEach(ticket => {
          if (!this.ticketOwners[ticket.key]) {
            this.$set(this.ticketOwners, ticket.key, { firstName: '', lastName: '' })
          }
        })
      },
    },
  },
  methods: {
    submit() {
      this.$v.$touch()
      this.ownerFieldsTouched = true
      if (!this.$v.$invalid && this.areTicketOwnersValid()) {
        this.$emit(
          'submit',
          this.paymentMethod,
          this.buyerEmail,
          this.ticketAdditionalNote || null,
          this.ticketPersonalization ? this.ticketOwnerPayload : null,
        )
      }
    },
    updateTicketOwner(ticketKey, field, value) {
      if (!this.ticketOwners[ticketKey]) {
        this.$set(this.ticketOwners, ticketKey, { firstName: '', lastName: '' })
      }
      this.$set(this.ticketOwners[ticketKey], field, value)
    },
    isTicketOwnerComplete(ticketKey) {
      const owner = this.ticketOwners[ticketKey] || {}

      return !!(
        owner.firstName &&
        owner.firstName.trim() &&
        owner.lastName &&
        owner.lastName.trim()
      )
    },
    showOwnerError(ticketKey, field) {
      const owner = this.ticketOwners[ticketKey] || {}

      return this.ownerFieldsTouched && !(owner[field] && owner[field].trim())
    },
    areTicketOwnersValid() {
      return (
        !this.ticketPersonalization ||
        this.ticketRows.every(ticket => this.isTicketOwnerComplete(ticket.key))
      )
    },
    calculateOrderTotalPrice() {
      let newPrice = Number(this.orderTotalPrice)
      if (this.paymentMethod === 'KEKS10') {
        newPrice = round(this.orderTotalPrice * 0.9, 2)
      } else if (['TELEGRAM20', 'POPUST20', 'CARD20', 'CASH20'].includes(this.paymentMethod)) {
        newPrice = round(this.orderTotalPrice * 0.8, 2)
      } else if (['Popust40'].includes(this.paymentMethod)) {
        newPrice = round(this.orderTotalPrice * 0.6, 2)
      } else if (this.paymentMethod === 'FREE') {
        newPrice = 0
      } else if (['Popust50', 'CASH50', 'CARD50'].includes(this.paymentMethod)) {
        newPrice = round(this.orderTotalPrice * 0.5, 2)
      }

      return (this.totalPrice = newPrice)
    },
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.modal-background {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #00000050;
}

.modal {
  text-align: left;
  background-color: white;
  width: 100%;
  position: relative;
  max-width: 50.125rem;
  border-radius: 12px;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  box-shadow: 0 0 24px rgba(51, 51, 51, 0.2);
}

.modal::-webkit-scrollbar {
  display: none;
}

.modal-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: 0;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #333333;
  margin: 0;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
}

.modal-heading {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.modal-description {
  color: #979797;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
}

.close-button {
  align-items: center;
  background: #ffffff;
  border: 0;
  border-radius: 48px;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  padding: 4px;
  width: 32px;
}

.field {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  display: flex;
  min-height: 56px;
  position: relative;
}

.field.invalid {
  border-color: #d32f2f;
}

.field-control {
  background: transparent;
  border: 0;
  color: #333333;
  flex: 1;
  font-family: 'Roboto';
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 24px;
  min-width: 0;
  outline: none;
  width: 100%;
}

.field-control::placeholder {
  color: #666666;
  opacity: 1;
}

.payment-field {
  padding: 0 48px 0 16px;
}

.payment-field select {
  appearance: none;
  cursor: pointer;
}

.payment-summary {
  color: #333333;
  font-family: 'Source Sans Pro';
  font-size: 16px;
  line-height: 1.5;
  margin: -16px 0 0;
}

.floating-label {
  background: #ffffff;
  color: #666666;
  font-family: 'Roboto';
  font-size: 12px;
  font-weight: 400;
  left: 12px;
  letter-spacing: 0.4px;
  line-height: 16px;
  padding: 0 4px;
  position: absolute;
  top: -9px;
}

.select-arrow {
  pointer-events: none;
  position: absolute;
  right: 16px;
  top: 18px;
}

.ticket-owner-list {
  border: 1px solid rgba(151, 151, 151, 0.5);
  border-radius: 4px;
  overflow: hidden;
}

.ticket-owner-card + .ticket-owner-card {
  border-top: 1px solid rgba(151, 151, 151, 0.5);
}

.ticket-owner-header {
  align-items: center;
  display: flex;
  gap: 24px;
  padding: 16px 24px;
}

.ticket-owner-title {
  color: #333333;
  flex: 1;
  font-family: 'Raleway';
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;
  min-width: 0;
}

.ticket-owner-fields {
  display: flex;
  gap: 20px;
  padding: 16px 24px 32px;
}

.owner-field {
  border-color: rgba(151, 151, 151, 0.5);
  flex: 1;
  min-width: 220px;
  padding: 0 15px;
}

.owner-field .floating-label {
  background: #ffffff;
  color: #49454f;
  left: 11px;
}

.owner-field .field-control {
  color: #1c1b1f;
}

.email-field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.email-field {
  gap: 16px;
  padding: 0 16px;
}

.field-hint {
  color: #666666;
  font-family: 'Roboto';
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.4px;
  line-height: 16px;
  margin: 0;
  padding: 0 19px;
}

.modal-actions {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding: 32px 24px 24px;
}

.continue-button {
  background: #0277bd;
  border: 0;
  border-radius: 24px;
  color: #ffffff;
  cursor: pointer;
  font-family: 'Raleway';
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  padding: 12px 24px;
  text-align: center;
  text-transform: uppercase;
}

.mail-icon {
  height: 24px;
  width: 24px;
}

@media screen and (max-width: 700px) {
  .modal {
    width: 100%;
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .modal-content {
    gap: 24px;
  }

  .modal-header,
  .ticket-owner-fields {
    flex-direction: column;
  }

  .close-button {
    position: absolute;
    right: 16px;
    top: 16px;
  }

  .modal-heading {
    padding-right: 32px;
  }

  .ticket-owner-header,
  .ticket-owner-fields {
    padding-left: 16px;
    padding-right: 16px;
  }

  .owner-field {
    min-width: 100%;
    width: 100%;
  }
}
</style>
